import { useState } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog.tsx';
import { Input } from './ui/input.tsx';
import { Label } from './ui/label.tsx';
import { AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { createLogger, toErrorMessage } from '@metron/shared';
import type { DataLinkAccount } from '../hooks/useDataLinkAccounts.ts';

const logger = createLogger('DeleteDataLinkAccountDialog');

interface DeleteDataLinkAccountDialogProps {
  account: DataLinkAccount | null;
  onOpenChange: (open: boolean) => void;
  onDelete: (username: string, confirmEmail: string) => Promise<void>;
}

export function DeleteDataLinkAccountDialog({
  account,
  onOpenChange,
  onDelete,
}: DeleteDataLinkAccountDialogProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [confirmationText, setConfirmationText] = useState('');

  // The account email is the confirmation phrase — typing "DELETE" is too easy
  // to do on autopilot, and the same value is re-checked server-side.
  const isConfirmed = account !== null &&
    confirmationText.trim().toLowerCase() === account.email.toLowerCase();

  const handleDelete = async () => {
    if (!account || !isConfirmed) return;

    setIsLoading(true);
    try {
      await onDelete(account.username, confirmationText.trim());
      toast.success(`DataLink account ${account.email} deleted`);
      setConfirmationText('');
      onOpenChange(false);
    } catch (error) {
      toast.error(toErrorMessage(error, 'Failed to delete DataLink account'));
      logger.error({ error }, 'Failed to delete DataLink account');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) setConfirmationText('');
    onOpenChange(open);
  };

  if (!account) return null;

  return (
    <AlertDialog open onOpenChange={handleOpenChange}>
      <AlertDialogContent className='max-w-md'>
        <AlertDialogHeader>
          <div className='flex items-center gap-3 mb-2'>
            <div className='flex h-10 w-10 items-center justify-center rounded-full bg-destructive/10'>
              <AlertTriangle className='h-5 w-5 text-destructive' />
            </div>
            <AlertDialogTitle className='text-destructive'>
              Delete DataLink Account
            </AlertDialogTitle>
          </div>
          <AlertDialogDescription asChild>
            <div className='space-y-4 text-left'>
              <div className='rounded-md bg-destructive/10 border border-destructive/20 p-3 text-sm'>
                <p className='font-semibold text-destructive mb-1'>
                  Warning: This action cannot be undone!
                </p>
                <p className='text-muted-foreground'>
                  The account's API credentials stop working immediately and any integration the
                  customer built against them will start failing. The account must be onboarded
                  again from scratch if access is needed later.
                </p>
              </div>

              <div className='space-y-1 text-sm'>
                <p>
                  <span className='text-muted-foreground'>Customer:</span>{' '}
                  <span className='font-medium'>
                    {account.customerName ?? `ID ${account.customerId}`}
                  </span>
                </p>
                <p>
                  <span className='text-muted-foreground'>Email:</span>{' '}
                  <span className='font-medium'>{account.email}</span>
                </p>
              </div>

              <div className='space-y-2'>
                <Label htmlFor='delete-confirmation' className='text-sm font-medium'>
                  Type the account email to confirm
                </Label>
                <Input
                  id='delete-confirmation'
                  value={confirmationText}
                  onChange={(e) => setConfirmationText(e.target.value)}
                  placeholder={account.email}
                  className='font-mono'
                  autoComplete='off'
                  disabled={isLoading}
                />
              </div>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isLoading} onClick={() => setConfirmationText('')}>
            Cancel
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => {
              // Radix closes the dialog on action click; keep it open so a failed
              // delete leaves the error visible instead of silently dismissing.
              e.preventDefault();
              void handleDelete();
            }}
            disabled={isLoading || !isConfirmed}
            className='bg-destructive text-destructive-foreground hover:bg-destructive/90'
          >
            {isLoading ? 'Deleting...' : 'Delete Account'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
